// Mobile Checkout Page
// Mobile version (<768px)

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { orderApi } from '@/services/orderApi';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import type { CartItem } from '@/types';
import styles from './MobileCheckoutPage.module.css';

interface FormData {
  recipientName: string;
  recipientPhone: string;
  recipientAddress: string;
  notes: string;
}

interface FormErrors {
  recipientName?: string;
  recipientPhone?: string;
  recipientAddress?: string;
}

export default function MobileCheckoutPage() {
  const { items, total, isLoading, refreshCart } = useCart();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    recipientName: '',
    recipientPhone: '',
    recipientAddress: '',
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Redirect if cart is empty
  useEffect(() => {
    if (!isLoading && items.length === 0) {
      navigate('/cart');
    }
  }, [items, isLoading, navigate]);

  // Sync cart on page load
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.recipientName.trim()) {
      errors.recipientName = '請輸入收件人姓名';
    }

    if (!formData.recipientPhone.trim()) {
      errors.recipientPhone = '請輸入聯絡電話';
    } else if (!/^09\d{8}$/.test(formData.recipientPhone.trim())) {
      errors.recipientPhone = '請輸入有效的手機號碼格式';
    }

    if (!formData.recipientAddress.trim()) {
      errors.recipientAddress = '請輸入配送地址';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name in formErrors) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };


  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await orderApi.createOrder({
        recipientName: formData.recipientName.trim(),
        recipientPhone: formData.recipientPhone.trim(),
        recipientAddress: formData.recipientAddress.trim(),
        notes: formData.notes.trim() || undefined,
      });

      navigate('/checkout/success', { state: { order: response.data } });
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立訂單失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.mobileCheckoutPage}>
        <div className={styles.loading}>載入中...</div>
      </div>
    );
  }

  return (
    <div className={styles.mobileCheckoutPage}>
      <MobileHeader
        title="結帳"
        showBack
        onBack={() => navigate('/cart')}
      />

      {/* Error message */}
      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={() => setError(null)} className={styles.closeButton}>×</button>
        </div>
      )}

      {/* Cart Items Summary */}
      <div className={styles.itemsSection}>
        <h2 className={styles.sectionTitle}>訂單商品 ({items.length})</h2>
        <div className={styles.itemsList}>
          {items.map((item: CartItem) => (
            <div key={item.id} className={styles.item}>
              <div className={styles.itemInfo}>
                <h3 className={styles.itemTitle}>{item.product.title}</h3>
                {item.variantAttributes && (
                  <p className={styles.itemVariant}>
                    {Object.entries(item.variantAttributes)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(', ')}
                  </p>
                )}
                <p className={styles.itemMeta}>數量: {item.quantity}</p>
              </div>
              <p className={styles.itemPrice}>{formatPrice(item.subtotal)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Checkout Form */}
      <form onSubmit={handleSubmit} className={styles.form}>
        <h2 className={styles.sectionTitle}>收件人資訊</h2>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            收件人姓名 <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="recipientName"
            value={formData.recipientName}
            onChange={handleInputChange}
            className={`${styles.input} ${formErrors.recipientName ? styles.inputError : ''}`}
            disabled={isSubmitting}
            placeholder="請輸入姓名"
          />
          {formErrors.recipientName && (
            <span className={styles.errorText}>{formErrors.recipientName}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            聯絡電話 <span className={styles.required}>*</span>
          </label>
          <input
            type="tel"
            name="recipientPhone"
            value={formData.recipientPhone}
            onChange={handleInputChange}
            placeholder="09xxxxxxxx"
            className={`${styles.input} ${formErrors.recipientPhone ? styles.inputError : ''}`}
            disabled={isSubmitting}
          />
          {formErrors.recipientPhone && (
            <span className={styles.errorText}>{formErrors.recipientPhone}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            配送地址 <span className={styles.required}>*</span>
          </label>
          <input
            type="text"
            name="recipientAddress"
            value={formData.recipientAddress}
            onChange={handleInputChange}
            placeholder="請輸入完整地址"
            className={`${styles.input} ${formErrors.recipientAddress ? styles.inputError : ''}`}
            disabled={isSubmitting}
          />
          {formErrors.recipientAddress && (
            <span className={styles.errorText}>{formErrors.recipientAddress}</span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>備註</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="特殊需求說明（選填）"
            rows={3}
            className={styles.textarea}
            disabled={isSubmitting}
          />
        </div>

        {/* Order Total */}
        <div className={styles.orderTotal}>
          <span className={styles.totalLabel}>總計</span>
          <span className={styles.totalValue}>{formatPrice(total)}</span>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? '建立訂單中...' : `確認訂單 ${formatPrice(total)}`}
        </button>
      </form>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
